import { useCallback, useEffect, useRef, useState } from 'react'
import type { AiActivitySseEvent, CursorPage } from '../services/adminApi.js'
import type { AccountEvent, AiToolCallEvent, SessionSummary } from '../types/index.js'

export type SessionCallsState = {
  events: AiToolCallEvent[]
  cursor: string | null
  hasMore: boolean
  loading: boolean
  loaded: boolean
}

export type ActivityLogApi = {
  getAccountEvents: (entityId: string) => Promise<AccountEvent[]>
  listSessions: (entityId: string, cursor?: string | null) => Promise<CursorPage<SessionSummary>>
  listSessionToolCalls: (entityId: string, conversationId: string, cursor?: string | null) => Promise<CursorPage<AiToolCallEvent>>
  streamActivity: (entityId: string, onEvent: (event: AiActivitySseEvent) => void, signal: AbortSignal) => Promise<void>
}

export type UseEntityActivityLogResult = {
  accountEvents: AccountEvent[]
  sessions: SessionSummary[]
  sessionsHasMore: boolean
  sessionsLoading: boolean
  loadMoreSessions: () => void
  sessionCalls: Record<string, SessionCallsState>
  loadSessionCalls: (conversationId: string, loadMore?: boolean) => void
  isLive: boolean
  error: string | null
}

const EMPTY_SESSION_CALLS: SessionCallsState = { events: [], cursor: null, hasMore: false, loading: false, loaded: false }

export function useEntityActivityLog(entityId: string | null, api: ActivityLogApi): UseEntityActivityLogResult {
  const [accountEvents, setAccountEvents] = useState<AccountEvent[]>([])
  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const [sessionsCursor, setSessionsCursor] = useState<string | null>(null)
  const [sessionsHasMore, setSessionsHasMore] = useState(false)
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [sessionCalls, setSessionCalls] = useState<Record<string, SessionCallsState>>({})
  const [isLive, setIsLive] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const activeId = useRef<string | null>(null)

  useEffect(() => {
    if (!entityId) return
    activeId.current = entityId

    setAccountEvents([])
    setSessions([])
    setSessionsCursor(null)
    setSessionsHasMore(false)
    setSessionCalls({})
    setIsLive(true)
    setError(null)

    const controller = new AbortController()

    api.getAccountEvents(entityId)
      .then((events) => {
        if (activeId.current === entityId) setAccountEvents(events)
      })
      .catch((err) => {
        if (activeId.current === entityId) setError(err instanceof Error ? err.message : 'Failed to load account events')
      })

    setSessionsLoading(true)
    api.listSessions(entityId)
      .then(({ data, nextCursor }) => {
        if (activeId.current !== entityId) return
        setSessions(data)
        setSessionsCursor(nextCursor)
        setSessionsHasMore(nextCursor !== null)
      })
      .catch((err) => {
        if (activeId.current === entityId) setError(err instanceof Error ? err.message : 'Failed to load sessions')
      })
      .finally(() => {
        if (activeId.current === entityId) setSessionsLoading(false)
      })

    api.streamActivity(
      entityId,
      (message) => {
        if (activeId.current !== entityId) return
        if (message.type === 'tool_call') {
          const event = message.event

          setSessions((prev) => {
            const idx = prev.findIndex((s) => s.conversationId === event.conversationId)
            if (idx === -1) {
              return [{ conversationId: event.conversationId, lastActivityAt: event.createdAt, callCount: 1 }, ...prev]
            }
            const existing = prev[idx]
            const updated: SessionSummary = { ...existing, lastActivityAt: event.createdAt, callCount: existing.callCount + 1 }
            const rest = prev.filter((_, i) => i !== idx)
            return [updated, ...rest]
          })

          setSessionCalls((prev) => {
            const current = prev[event.conversationId]
            if (!current || !current.loaded) return prev
            if (current.events.some((e) => e.id === event.id)) return prev
            return { ...prev, [event.conversationId]: { ...current, events: [...current.events, event] } }
          })
        } else if (message.type === 'error') {
          setError(message.message)
        }
      },
      controller.signal,
    ).catch((err) => {
      if (controller.signal.aborted) return
      setIsLive(false)
      setError(err instanceof Error ? err.message : 'Stream failed')
    }).then(() => {
      if (!controller.signal.aborted) setIsLive(false)
    })

    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId])

  const loadMoreSessions = useCallback(() => {
    if (!entityId || sessionsLoading || !sessionsHasMore) return
    setSessionsLoading(true)
    api.listSessions(entityId, sessionsCursor)
      .then(({ data, nextCursor }) => {
        if (activeId.current !== entityId) return
        setSessions((prev) => [...prev, ...data])
        setSessionsCursor(nextCursor)
        setSessionsHasMore(nextCursor !== null)
      })
      .catch((err) => {
        if (activeId.current === entityId) setError(err instanceof Error ? err.message : 'Failed to load sessions')
      })
      .finally(() => {
        if (activeId.current === entityId) setSessionsLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, sessionsCursor, sessionsHasMore, sessionsLoading])

  const loadSessionCalls = useCallback((conversationId: string, loadMore = false) => {
    if (!entityId) return
    const current = sessionCalls[conversationId] ?? EMPTY_SESSION_CALLS
    if (current.loading) return
    if (loadMore && !current.hasMore) return
    if (!loadMore && current.loaded) return

    setSessionCalls((prev) => ({ ...prev, [conversationId]: { ...current, loading: true } }))

    api.listSessionToolCalls(entityId, conversationId, loadMore ? current.cursor : null)
      .then(({ data, nextCursor }) => {
        if (activeId.current !== entityId) return
        setSessionCalls((prev) => {
          const prevState = prev[conversationId] ?? EMPTY_SESSION_CALLS
          const merged = loadMore ? [...data, ...prevState.events] : data
          return { ...prev, [conversationId]: { events: merged, cursor: nextCursor, hasMore: nextCursor !== null, loading: false, loaded: true } }
        })
      })
      .catch((err) => {
        if (activeId.current !== entityId) return
        setError(err instanceof Error ? err.message : 'Failed to load session activity')
        setSessionCalls((prev) => ({ ...prev, [conversationId]: { ...(prev[conversationId] ?? EMPTY_SESSION_CALLS), loading: false } }))
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, sessionCalls])

  return {
    accountEvents,
    sessions,
    sessionsHasMore,
    sessionsLoading,
    loadMoreSessions,
    sessionCalls,
    loadSessionCalls,
    isLive,
    error,
  }
}
